import React, { useState, useEffect, useRef } from 'react';
import PromotionCard from '../components/PromotionCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import api from '../services/api';
import { API_ENDPOINTS } from '../utils/constants';
import { FiSearch, FiChevronLeft, FiChevronRight, FiFilter, FiX } from 'react-icons/fi';

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [filteredPromotions, setFilteredPromotions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const categoryScrollRef = useRef(null);
  const [canScrollCategoryLeft, setCanScrollCategoryLeft] = useState(false);
  const [canScrollCategoryRight, setCanScrollCategoryRight] = useState(true);

  // Fetch categories
  useEffect(() => {
    fetchCategories();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch promotions when search or category changes
  useEffect(() => {
    fetchPromotions();
  }, [debouncedSearch, selectedCategory]);

  // Filter promotions locally after fetch
  useEffect(() => {
    if (promotions.length > 0) {
      let filtered = [...promotions];
      
      // Filter by category
      if (selectedCategory) {
        filtered = filtered.filter(p => p.category_id === selectedCategory);
      }
      
      // Filter by search term (already handled in API, but for safety)
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        filtered = filtered.filter(p => 
          p.title.toLowerCase().includes(searchLower) ||
          p.description?.toLowerCase().includes(searchLower)
        );
      }
      
      setFilteredPromotions(filtered);
    }
  }, [promotions, selectedCategory, debouncedSearch]);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await api.get(API_ENDPOINTS.CATEGORIES);
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      
      // Using promotions endpoint or fallback to products with discount
      try {
        const response = await api.get(API_ENDPOINTS.PROMOTIONS || API_ENDPOINTS.PRODUCTS, { 
          params: { ...params, discount: true, limit: 100 }
        });
        let promoData = response.data;
        
        // Ensure we only show products with discounts
        if (Array.isArray(promoData)) {
          promoData = promoData.filter(product => product.discount_percent > 0);
        }
        setPromotions(promoData);
      } catch (err) {
        // Fallback: fetch products with discount
        const response = await api.get(API_ENDPOINTS.PRODUCTS, { 
          params: { ...params, limit: 100 }
        });
        const discountedProducts = response.data.filter(product => product.discount_percent > 0);
        setPromotions(discountedProducts);
      }
    } catch (err) {
      setError('មិនអាចផ្ទុកការប្រម៉ូសិនបានទេ');
      console.error('Error fetching promotions:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setDebouncedSearch('');
  };

  const hasActiveFilters = searchTerm || selectedCategory;

  // Category scroll functions
  const checkCategoryScrollButtons = () => {
    if (categoryScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoryScrollRef.current;
      setCanScrollCategoryLeft(scrollLeft > 0);
      setCanScrollCategoryRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollCategory = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = categoryScrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      categoryScrollRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      setTimeout(checkCategoryScrollButtons, 300);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
          ការ<span className="text-black">ប្រម៉ូសិន</span>
        </h1>
        <p className="text-gray-600 text-sm max-w-2xl mx-auto">
          ជ្រើសរើសសម្លៀកបំពាក់ដែលកំពុងផ្សព្វផ្សាយតម្លៃពិសេស 🎉✨
        </p>
      </div>

      {/* Category Filter - Horizontal Scroll */}
      {!loadingCategories && categories.length > 0 && (
        <div className="mb-6 relative">
          <div className="flex items-center">
            {canScrollCategoryLeft && (
              <button 
                onClick={() => scrollCategory('left')}
                className="absolute left-0 z-10 w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-black hover:text-white hover:border-black transition-colors"
              >
                <FiChevronLeft size={18} />
              </button>
            )}
            
            <div 
              ref={categoryScrollRef}
              className="flex gap-2 overflow-x-auto scrollbar-hide px-8 py-2"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onScroll={checkCategoryScrollButtons}
            >
              <button
                onClick={() => setSelectedCategory('')}
                className={`flex-none px-4 py-2 text-sm border whitespace-nowrap transition-colors rounded ${
                  selectedCategory === '' 
                    ? 'bg-black text-white border-black' 
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-black hover:border-black'
                }`}
              >
                ទាំងអស់
              </button>
              
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex-none px-4 py-2 text-sm border whitespace-nowrap transition-colors rounded ${
                    selectedCategory === category.id 
                      ? 'bg-black text-white border-black' 
                      : 'border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-black hover:border-black'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {canScrollCategoryRight && (
              <button 
                onClick={() => scrollCategory('right')}
                className="absolute right-0 z-10 w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-black hover:text-white hover:border-black transition-colors"
              >
                <FiChevronRight size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="mb-8 flex flex-col sm:flex-row gap-4 items-center justify-between">
        {/* Search Input */}
        <div className="relative max-w-md w-full">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="ស្វែងរកប្រម៉ូសិន..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 bg-gray-50 text-gray-700 text-sm focus:outline-none focus:border-black focus:bg-white transition-colors rounded"
          />
        </div>

        {/* Mobile Filter Button */}
        <button 
          onClick={() => setShowMobileFilters(true)}
          className="sm:hidden flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-200 text-sm hover:bg-black hover:text-white hover:border-black transition-colors rounded"
        >
          <FiFilter size={16} />
          តម្រងតាមប្រភេទ
          {hasActiveFilters && (
            <span className="w-2 h-2 bg-black rounded-full"></span>
          )}
        </button>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2">
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 border border-gray-200 text-xs text-black rounded">
                ស្វែងរក: {searchTerm}
                <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-gray-700">×</button>
              </span>
            )}
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 border border-gray-200 text-xs text-black rounded">
                {categories.find(c => c.id === selectedCategory)?.name}
                <button onClick={() => setSelectedCategory('')} className="ml-1 hover:text-gray-700">×</button>
              </span>
            )}
            <button 
              onClick={clearFilters}
              className="text-xs text-gray-500 underline hover:text-black"
            >
              សម្អាតទាំងអស់
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      {!loading && !error && (
        <p className="mb-4 text-sm text-gray-500 text-center">
          បង្ហាញ <span className="font-semibold text-gray-700">{filteredPromotions.length}</span> ការប្រម៉ូសិន
        </p>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <ErrorAlert message={error} />
      ) : (
        <>
          {filteredPromotions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-6xl mb-4">🎉</div>
              <p className="text-gray-500 text-base">រកមិនឃើញការប្រម៉ូសិន</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-3 text-black text-sm hover:text-gray-700 underline"
                >
                  សម្អាតតម្រងទាំងអស់
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPromotions.map((promotion) => (
                <PromotionCard key={promotion.id} promotion={promotion} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Promotional Banner */}
      {!loading && filteredPromotions.length > 0 && (
        <div className="mt-12 bg-black text-white p-6 rounded-lg text-center">
          <h3 className="text-xl font-bold mb-2">កុំខកខានឱកាសល្អ!</h3>
          <p className="text-gray-300 text-sm mb-4">
            ការប្រម៉ូសិនមានកំណត់ពេលវេលា សូមប្រញាប់ទិញទំនិញឥឡូវនេះ
          </p>
          <div className="flex gap-2 justify-center text-2xl">
            🏃‍♂️ 💨 🛍️ ✨
          </div>
        </div>
      )}

      {/* Mobile Filter Drawer */}
      {showMobileFilters && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 sm:hidden"
          onClick={() => setShowMobileFilters(false)}
        >
          <div 
            className="absolute right-0 top-0 bottom-0 w-4/5 max-w-sm bg-white p-5 overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-200">
              <span className="font-semibold text-gray-800">ជ្រើសរើសប្រភេទ</span>
              <button 
                onClick={() => setShowMobileFilters(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <div 
                onClick={() => {
                  setSelectedCategory('');
                  setShowMobileFilters(false);
                }}
                className={`flex items-center gap-2 cursor-pointer p-2 rounded transition-colors ${
                  selectedCategory === '' ? 'bg-gray-100 text-black' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className={`w-4 h-4 border rounded transition-colors ${
                  selectedCategory === '' ? 'bg-black border-black' : 'border-gray-300'
                }`}>
                  {selectedCategory === '' && <span className="flex items-center justify-center text-white text-xs">✓</span>}
                </div>
                <span className="text-sm">ទាំងអស់</span>
              </div>

              {categories.map((category) => (
                <div 
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setShowMobileFilters(false);
                  }}
                  className={`flex items-center gap-2 cursor-pointer p-2 rounded transition-colors ${
                    selectedCategory === category.id ? 'bg-gray-100 text-black' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-4 h-4 border rounded transition-colors ${
                    selectedCategory === category.id ? 'bg-black border-black' : 'border-gray-300'
                  }`}>
                    {selectedCategory === category.id && <span className="flex items-center justify-center text-white text-xs">✓</span>}
                  </div>
                  <span className="text-sm">{category.name}</span>
                </div>
              ))}
            </div>

            {hasActiveFilters && (
              <button
                onClick={() => {
                  clearFilters();
                  setShowMobileFilters(false);
                }}
                className="w-full mt-6 bg-gray-100 border border-gray-200 py-3 text-sm hover:bg-black hover:text-white hover:border-black transition-colors rounded"
              >
                សម្អាតតម្រងទាំងអស់
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Promotions;